import { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import { getPlatformStats } from "../../api/admin";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/type";
import { MarginBottom } from "../MarginBottom";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { motion } from "framer-motion";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const StatsContainer = styled.div`
  width: 100%;
  background: #121212;
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 1rem;
  overflow-x: auto;
`;

const StatsTitle = styled.h2`
  color: #ccff00;
  margin-bottom: 1.5rem;
  font-size: 1.5rem;
  font-weight: 700;
  background: linear-gradient(90deg, #ccff00, #00b4db);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;

  @media (max-width: 768px) {
    font-size: 1.3rem;
    margin-bottom: 1rem;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const StatCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 1.2rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
    border-color: rgba(0, 224, 255, 0.3);
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 3px;
    height: 100%;
    background: linear-gradient(to bottom, #ccff00, #00b4db);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover::before {
    opacity: 1;
  }

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const StatTitle = styled.h3`
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.95rem;
  margin-bottom: 0.5rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const StatValue = styled.div`
  color: white;
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  font-feature-settings: "tnum";

  @media (max-width: 768px) {
    font-size: 1.6rem;
  }
`;

const StatChange = styled.div<{ $positive?: boolean }>`
  color: ${(props) => (props.$positive ? "#00ff9d" : "#ff4d4d")};
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: 0.3rem;

  &::before {
    content: "${(props) => (props.$positive ? "↑" : "↓")}";
    display: inline-block;
  }

  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 1.1rem;
`;

const TimeFilter = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
`;

const TimeButton = styled.button<{ $active?: boolean }>`
  padding: 0.5rem 1rem;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: ${(props) =>
    props.$active ? "rgba(0, 224, 255, 0.1)" : "rgba(255, 255, 255, 0.05)"};
  color: ${(props) => (props.$active ? "#ccff00" : "rgba(255, 255, 255, 0.7)")};
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.85rem;

  &:hover {
    border-color: rgba(0, 224, 255, 0.3);
  }

  @media (max-width: 768px) {
    padding: 0.4rem 0.8rem;
    font-size: 0.8rem;
  }
`;

const ChartContainer = styled.div`
  grid-column: 1 / -1;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  padding: 1.2rem;
  margin-top: 1.5rem;
  height: 420px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(0, 224, 255, 0.3);
  }

  @media (max-width: 768px) {
    padding: 1rem;
    height: 320px;
    margin-top: 1rem;
  }
`;

// Add proper type definitions
interface PlatformStats {
  totalUsers: number;
  activeUsers: number;
  tasksCompleted: number;
  totalRewards: number;
  newUsers: number;
  avgSessionDuration: number;
  userGrowth: number;
  activeUserChange: number;
  taskCompletionRate: number;
  rewardGrowth: number;
  newUserGrowth: number;
  sessionDurationChange: number;
}

interface ChartData {
  labels: string[];
  data: number[];
}

interface StatsResponse {
  stats: PlatformStats;
  chartData?: ChartData;
}

const AdminStats = () => {
  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [chartData, setChartData] = useState<ChartData | null>(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<"24h" | "7d" | "30d" | "90d">(
    "7d"
  );
  const currentAdmin = useSelector((state: RootState) => state.userData);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);

        const data = await getPlatformStats(currentAdmin.uid, timeRange);

        if (!data) {
          setStats(null);
          setChartData(null);
          return;
        }

        // Handle the response based on your API's actual structure
        if (data.stats) {
          setStats(data.stats);
        }
        if (data.chartData) {
          setChartData(data.chartData);
        } else {
          setChartData(null);
        }
      } catch (error) {
        console.error("Error fetching stats:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [timeRange]);

  if (loading) {
    return (
      <StatsContainer>
        <StatsTitle>Platform Statistics</StatsTitle>
        <LoadingContainer>Loading statistics...</LoadingContainer>
      </StatsContainer>
    );
  }

  return (
    <>
      <StatsContainer>
        <StatsTitle>Platform Statistics</StatsTitle>

        <TimeFilter>
          <TimeButton
            $active={timeRange === "24h"}
            onClick={() => setTimeRange("24h")}
          >
            24 Hours
          </TimeButton>
          <TimeButton
            $active={timeRange === "7d"}
            onClick={() => setTimeRange("7d")}
          >
            7 Days
          </TimeButton>
          <TimeButton
            $active={timeRange === "30d"}
            onClick={() => setTimeRange("30d")}
          >
            30 Days
          </TimeButton>
          <TimeButton
            $active={timeRange === "90d"}
            onClick={() => setTimeRange("90d")}
          >
            90 Days
          </TimeButton>
        </TimeFilter>

        <StatsGrid>
          <StatCard>
            <StatTitle>
              <span>👥</span> Total Users
            </StatTitle>
            <StatValue>{stats?.totalUsers?.toLocaleString() || "0"}</StatValue>
            <StatChange $positive={stats?.userGrowth >= 0}>
              {Math.abs(stats?.userGrowth || 0).toFixed(2)}%{" "}
              {timeRange === "24h" ? "today" : "this period"}
            </StatChange>
          </StatCard>

          <StatCard>
            <StatTitle>
              <span>🔥</span> Active Users
            </StatTitle>
            <StatValue>{stats?.activeUsers?.toLocaleString() || "0"}</StatValue>
            <StatChange $positive={stats?.activeUserChange >= 0}>
              {Math.abs(stats?.activeUserChange || 0).toFixed(2)}% from previous
              period
            </StatChange>
          </StatCard>

          <StatCard>
            <StatTitle>
              <span>✅</span> Tasks Completed
            </StatTitle>
            <StatValue>
              {stats?.tasksCompleted?.toLocaleString() || "0"}
            </StatValue>
          </StatCard>

          <StatCard>
            <StatTitle>
              <span>💰</span> Total Rewards
            </StatTitle>
            <StatValue>
              {stats?.totalRewards?.toLocaleString() || "0"}
            </StatValue>
            <StatChange $positive={stats?.rewardGrowth >= 0}>
              {Math.abs(stats?.rewardGrowth || 0).toFixed(2)}% from previous
              period
            </StatChange>
          </StatCard>

          <StatCard>
            <StatTitle>
              <span>🆕</span> New Users
            </StatTitle>
            <StatValue>{stats?.newUsers?.toLocaleString() || "0"}</StatValue>
            <StatChange $positive={stats?.newUserGrowth >= 0}>
              {Math.abs(stats?.newUserGrowth || 0).toFixed(2)}% growth
            </StatChange>
          </StatCard>
        </StatsGrid>

        <ChartContainer>
          <StatTitle>
            {timeRange === "24h" ? "Hourly" : "Daily"} User Activity (
            {
              {
                "24h": "Last 24 Hours",
                "7d": "Last 7 Days",
                "30d": "Last 30 Days",
                "90d": "Last 90 Days",
              }[timeRange]
            }
            )
          </StatTitle>

          {chartData && chartData.labels && chartData.data ? (
            <motion.div
              key={timeRange}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              style={{ width: "100%", height: "calc(100% - 40px)" }}
            >
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={chartData.labels.map((label, i) => ({
                    label,
                    value: chartData.data[i],
                  }))}
                  margin={{ top: 20, right: 20, left: 0, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="colorGlow" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#00e0ff" stopOpacity={0.9} />
                      <stop
                        offset="100%"
                        stopColor="#ccff00"
                        stopOpacity={0.2}
                      />
                    </linearGradient>
                  </defs>

                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="rgba(255,255,255,0.05)"
                  />
                  <XAxis
                    dataKey="label"
                    stroke="rgba(255,255,255,0.6)"
                    tick={{ fontSize: 12 }}
                    angle={-15}
                    dy={5}
                  />
                  <YAxis
                    stroke="rgba(255,255,255,0.6)"
                    tick={{ fontSize: 12 }}
                    width={40}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "rgba(20,20,20,0.9)",
                      border: "1px solid rgba(0,224,255,0.3)",
                      borderRadius: "8px",
                      color: "#fff",
                    }}
                    labelStyle={{ color: "#ccff00" }}
                  />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="url(#colorGlow)"
                    strokeWidth={3}
                    dot={{ r: 4, stroke: "#00e0ff", strokeWidth: 2 }}
                    activeDot={{
                      r: 6,
                      stroke: "#ccff00",
                      strokeWidth: 2,
                      fill: "#121212",
                    }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </motion.div>
          ) : (
            <div
              style={{
                height: "calc(100% - 30px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "rgba(255,255,255,0.3)",
              }}
            >
              No chart data available
            </div>
          )}
        </ChartContainer>
      </StatsContainer>
      <MarginBottom />
    </>
  );
};

export default AdminStats;
